import { useId } from "react";
import type { ReactNode } from "react";
import { classNames } from "./internal/class-names";

export type CardVariant = "default" | "flat" | "raised";

export interface CardProps {
  /** Card heading. When given, the card becomes a named landmark region. */
  readonly title?: ReactNode | undefined;
  /**
   * Short uppercase kicker above the title, naming the kind of thing the card
   * holds. Rendered inside the header band, above the heading.
   */
  readonly eyebrow?: ReactNode | undefined;
  /** Heading level, so callers keep a correct document outline. */
  readonly headingLevel?: 2 | 3 | 4 | 5 | undefined;
  /** Controls rendered opposite the heading. */
  readonly actions?: ReactNode | undefined;
  readonly footer?: ReactNode | undefined;
  readonly variant?: CardVariant | undefined;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

/** Bounded content container. Renders a `<section>` labelled by its heading. */
export function Card({
  actions,
  children,
  className,
  eyebrow,
  footer,
  headingLevel = 2,
  title,
  variant = "default",
}: CardProps): ReactNode {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const Heading = `h${String(headingLevel)}` as "h2" | "h3" | "h4" | "h5";

  return (
    <section
      aria-labelledby={title === undefined ? undefined : titleId}
      className={classNames("thw-card", variant !== "default" && `thw-card--${variant}`, className)}
    >
      {title === undefined && actions === undefined && eyebrow === undefined ? null : (
        <div className="thw-card__header">
          <div className="thw-card__heading">
            {eyebrow === undefined ? null : (
              <p className="thw-card__eyebrow">{eyebrow}</p>
            )}
            {title === undefined ? null : (
              <Heading className="thw-card__title" id={titleId}>
                {title}
              </Heading>
            )}
          </div>
          {actions === undefined ? null : (
            <div className="thw-card__actions">{actions}</div>
          )}
        </div>
      )}
      {children}
      {footer === undefined ? null : <div className="thw-card__footer">{footer}</div>}
    </section>
  );
}
